/**
 * Central path bus for Adam control routing (MIDI / OSC / UI / learn).
 * Ported from Shady's ControlRegistry — exact-path binds, no wildcards.
 */

export type ControlMessage = {
  path: string;
  args: number[];
  stringArgs?: string[];
  timestamp: number;
  source?: string;
};

export type ControlBinding = {
  apply: (message: ControlMessage) => void;
};

export type ControlListener = (message: ControlMessage) => void;
export type ControlHistoryListener = (messages: ControlMessage[]) => void;

export function normalizeControlPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) {
    return "";
  }
  let normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  normalized = normalized.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function createControlMessage(
  path: string,
  args: number[],
  timestamp: number = Date.now(),
  source?: string,
): ControlMessage {
  return {
    path,
    args,
    timestamp,
    ...(source ? { source } : {}),
  };
}

export class ControlRegistry {
  private static readonly HISTORY_LIMIT = 1000;
  private bindings: Map<string, Set<ControlBinding>> = new Map();
  private listeners: Set<ControlListener> = new Set();
  private history: ControlMessage[] = [];
  private historyListeners: Set<ControlHistoryListener> = new Set();
  private bridgeForwardingSuspendCount = 0;

  bind(path: string, binding: ControlBinding): () => void {
    const normalized = normalizeControlPath(path);
    if (!normalized) {
      return () => {};
    }
    const existing = this.bindings.get(normalized);
    if (existing) {
      existing.add(binding);
    } else {
      this.bindings.set(normalized, new Set([binding]));
    }
    return () => this.unbind(normalized, binding);
  }

  unbind(path: string, binding: ControlBinding): void {
    const normalized = normalizeControlPath(path);
    const existing = this.bindings.get(normalized);
    if (!existing) {
      return;
    }
    existing.delete(binding);
    if (existing.size === 0) {
      this.bindings.delete(normalized);
    }
  }

  dispatch(message: ControlMessage): number {
    const normalized = normalizeControlPath(message.path);
    if (!normalized) {
      return 0;
    }
    const normalizedMessage =
      normalized === message.path ? message : { ...message, path: normalized };
    this.pushHistory(normalizedMessage);
    if (this.listeners.size > 0) {
      this.listeners.forEach(listener => listener(normalizedMessage));
    }
    const existing = this.bindings.get(normalized);
    if (!existing || existing.size === 0) {
      return 0;
    }
    let invoked = 0;
    existing.forEach(binding => {
      binding.apply(normalizedMessage);
      invoked += 1;
    });
    return invoked;
  }

  clear(path?: string): void {
    if (!path) {
      this.bindings.clear();
      return;
    }
    this.bindings.delete(normalizeControlPath(path));
  }

  subscribe(listener: ControlListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getHistory(): ControlMessage[] {
    return this.history.slice();
  }

  clearHistory(): void {
    if (this.history.length === 0) {
      return;
    }
    this.history = [];
    this.notifyHistoryListeners();
  }

  subscribeHistory(listener: ControlHistoryListener): () => void {
    listener(this.getHistory());
    this.historyListeners.add(listener);
    return () => {
      this.historyListeners.delete(listener);
    };
  }

  suspendBridgeForwarding(): () => void {
    this.bridgeForwardingSuspendCount += 1;
    let active = true;
    return () => {
      if (!active) {
        return;
      }
      active = false;
      this.bridgeForwardingSuspendCount = Math.max(
        0,
        this.bridgeForwardingSuspendCount - 1,
      );
    };
  }

  isBridgeForwardingSuspended(): boolean {
    return this.bridgeForwardingSuspendCount > 0;
  }

  private pushHistory(message: ControlMessage): void {
    this.history.push(message);
    const overflow = this.history.length - ControlRegistry.HISTORY_LIMIT;
    if (overflow > 0) {
      this.history.splice(0, overflow);
    }
    this.notifyHistoryListeners();
  }

  private notifyHistoryListeners(): void {
    if (this.historyListeners.size === 0) {
      return;
    }
    const snapshot = this.getHistory();
    this.historyListeners.forEach(listener => listener(snapshot));
  }
}

/** App-wide singleton — MIDI / OSC / learn / UI emit all share this bus. */
export const controlRegistry = new ControlRegistry();
